// CB:BEGIN admin-documents-page
import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../lib/getApiUrl';

const DOC_TYPE_LABELS: Record<string, string> = {
  CONTRACTOR_LICENSE: 'Contractor License',
  CONTRACTOR_INSURANCE_COI: 'Insurance Certificate (COI)',
  CONTRACTOR_W9: 'W-9',
  CONTRACTOR_AGREEMENT_SIGNED: 'Platform Agreement Signed',
  CONTRACTOR_ACH_AUTHORIZATION: 'ACH Authorization',
  HOMEOWNER_PROJECT_AGREEMENT: 'Homeowner Project Agreement',
  HOMEOWNER_CONTRACT_SIGNED: 'Contract Signed',
  HOMEOWNER_CONTRACT_SIGNED_MANUAL: 'Contract Signed (Manual)',
  ROOF_REPORT: 'Roof Measurement Report',
  MILESTONE_1_EVIDENCE: 'Milestone 1 Evidence',
  MILESTONE_2_EVIDENCE: 'Milestone 2 Evidence',
  MILESTONE_3_EVIDENCE: 'Milestone 3 Evidence',
  WARRANTY: 'Warranty Document',
  CHANGE_ORDER: 'Change Order',
  PERMIT: 'Permit',
  FINAL_COMPLETION: 'Final Completion',
  OTHER: 'Other',
};

const ALL_DOC_TYPES = Object.keys(DOC_TYPE_LABELS);

interface DocumentRow {
  id: string;
  documentType: string | null;
  linkedEntityType: string | null;
  linkedEntityId: string | null;
  projectId: string | null;
  contractorId: string | null;
  homeownerId: string | null;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  expiresAt: string | null;
  verifiedAt: string | null;
  uploadedBy: string;
  lastDownloadedAt: string | null;
  downloadCount: number;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const AdminDocumentsPage: React.FC<{ onNavigate?: (screen: string) => void }> = ({ onNavigate }) => {
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  // Filters
  const [filters, setFilters] = useState({
    type: '',
    linkedEntityType: '',
    contractorId: '',
    homeownerId: '',
    projectId: '',
    page: 1,
  });

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.linkedEntityType) params.append('linkedEntityType', filters.linkedEntityType);
      if (filters.contractorId) params.append('contractorId', filters.contractorId);
      if (filters.homeownerId) params.append('homeownerId', filters.homeownerId);
      if (filters.projectId) params.append('projectId', filters.projectId);
      params.append('page', filters.page.toString());
      params.append('limit', '50');

      const url = getApiUrl(`/api/admin/documents?${params.toString()}`);
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch documents');
      const d = await r.json();
      setDocuments(d.documents || []);
      setPagination(d.pagination);
    } catch (e) {
      console.error('Failed to fetch documents:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [filters.page]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const applyFilters = () => {
    fetchDocuments();
  };

  // CB:BEGIN admin-documents-view-action
  const viewDocument = async (doc: DocumentRow) => {
    try {
      const r = await fetch(getApiUrl(`/api/admin/documents/${doc.id}/download`), {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to get document URL');
      const d = await r.json();
      if (d.downloadUrl) {
        window.open(d.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      console.error('Failed to open document:', e);
      alert('Failed to open document');
    }
  };
  // CB:END admin-documents-view-action

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div style={{ padding: '20px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px' }}>
        📄 Document Management
      </h1>

      {/* Filters */}
      <div
        style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px',
        }}
      >
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Filters</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#6b7280' }}>
              Document Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white',
              }}
            >
              <option value="">All Types</option>
              {ALL_DOC_TYPES.map((t) => (
                <option key={t} value={t}>{DOC_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#6b7280' }}>
              Entity Type
            </label>
            <input
              type="text"
              value={filters.linkedEntityType}
              onChange={(e) => handleFilterChange('linkedEntityType', e.target.value)}
              placeholder="e.g., CONTRACTOR"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#6b7280' }}>
              Contractor ID
            </label>
            <input
              type="text"
              value={filters.contractorId}
              onChange={(e) => handleFilterChange('contractorId', e.target.value)}
              placeholder="Enter contractor ID"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#6b7280' }}>
              Homeowner ID
            </label>
            <input
              type="text"
              value={filters.homeownerId}
              onChange={(e) => handleFilterChange('homeownerId', e.target.value)}
              placeholder="Enter homeowner ID"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#6b7280' }}>
              Project ID
            </label>
            <input
              type="text"
              value={filters.projectId}
              onChange={(e) => handleFilterChange('projectId', e.target.value)}
              placeholder="Enter project ID"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
        </div>
        <button
          onClick={applyFilters}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
        >
          Apply Filters
        </button>
      </div>

      {/* Results Summary */}
      {pagination && (
        <div style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
          Showing {documents.length} of {pagination.total} documents (Page {pagination.page} of{' '}
          {pagination.totalPages})
        </div>
      )}

      {/* Documents Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div
            style={{
              display: 'inline-block',
              width: '32px',
              height: '32px',
              border: '3px solid #e5e7eb',
              borderTopColor: '#2563eb',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: '12px', color: '#6b7280' }}>Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#6b7280', fontSize: '16px' }}>No documents found</p>
          <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '8px' }}>
            Try adjusting your filters or create some documents
          </p>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          {/* CB:BEGIN admin-documents-project-grouping */}
          {/*
            Organize by project where possible:
            - Show project id in its own column
            - Sort rows by projectId (when present) then createdAt desc
          */}
          {(() => {
            const sortedDocuments = [...documents].sort((a, b) => {
              const ap = a.projectId || '';
              const bp = b.projectId || '';
              if (ap && !bp) return -1;
              if (!ap && bp) return 1;
              const cmp = ap.localeCompare(bp);
              if (cmp !== 0) return cmp;
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });

            return (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      Type
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      Project
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      File Name
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      Entity
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      Size
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      Uploaded
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      Expires
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.map((doc) => (
                    <tr key={doc.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <span
                          style={{
                            background: '#eff6ff',
                            color: '#1e40af',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                          }}
                        >
                          {DOC_TYPE_LABELS[doc.documentType || ''] || doc.documentType || 'Other'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280', fontFamily: 'monospace' }}>
                        {doc.projectId ? (
                          <span title={doc.projectId}>{doc.projectId.slice(0, 8)}...</span>
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#374151' }}>
                        {doc.fileName || 'Unnamed'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>
                        {doc.linkedEntityType ? (
                          <div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{doc.linkedEntityType}</div>
                            <div style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                              {doc.linkedEntityId?.slice(0, 8)}...
                            </div>
                          </div>
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>
                        {formatFileSize(doc.sizeBytes)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>
                        {formatDate(doc.createdAt)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>
                        {doc.expiresAt ? (
                          <span
                            style={{
                              color: new Date(doc.expiresAt) < new Date() ? '#dc2626' : '#6b7280',
                            }}
                          >
                            {formatDate(doc.expiresAt)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => viewDocument(doc)}
                          style={{
                            padding: '6px 12px',
                            background: 'white',
                            color: '#2563eb',
                            border: '1px solid #2563eb',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
          {/* CB:END admin-documents-project-grouping */}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <button
            onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
            disabled={filters.page === 1}
            style={{
              padding: '8px 16px',
              background: filters.page === 1 ? '#e5e7eb' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: filters.page === 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Previous
          </button>
          <span style={{ display: 'flex', alignItems: 'center', padding: '0 16px', fontSize: '14px', color: '#6b7280' }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: Math.min(pagination.totalPages, filters.page + 1) })}
            disabled={filters.page === pagination.totalPages}
            style={{
              padding: '8px 16px',
              background: filters.page === pagination.totalPages ? '#e5e7eb' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: filters.page === pagination.totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            Next
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminDocumentsPage;
// CB:END admin-documents-page
